/**
 * Mobile sticky buy bar (PDP, max-width 767px only).
 *
 * Adds <sticky-buy-mobile> inside the theme's .product-sticky-form__card and, on mobile,
 * shows it in place of the theme row: product name, the chosen size (or a "Select a size"
 * prompt) and one clean CTA. Desktop keeps the theme row untouched.
 *
 * It never adds to cart by itself. "Select a size" scrolls to the size picker and opens it;
 * "Add to Cart" presses the theme's own sticky submit button (or the main one), so the
 * theme's product-form, cart drawer, error messages and analytics run exactly as before.
 *
 * Every layout rule depends on :has() and the is-ready class, so if this script fails or
 * the browser lacks :has(), the theme's original row is what shows.
 */
(() => {
  'use strict';

  if (window.__stickyBuyMobileLoaded) return;
  window.__stickyBuyMobileLoaded = true;

  const TAG = 'sticky-buy-mobile';
  const MOBILE = window.matchMedia('(max-width: 767px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const GATE_OPTIONS = ['Size', 'Width']; // same names add-to-cart-button.js gates on
  const TEXT = {
    selectSize: 'Select a size',
    selectWidth: 'Select a width',
    addToCart: 'Add to Cart',
    soldOut: 'Sold Out',
    preOrder: 'Pre-order'
  };

  const CSS = `
${TAG}{display:none}
@media screen and (max-width:767px){
product-sticky-form:has(>.product-sticky-form__card>${TAG}.is-ready){padding-left:0!important;padding-right:0!important;padding-bottom:0!important}
.product-sticky-form__card:has(>${TAG}.is-ready){display:grid!important;grid-template-columns:minmax(0,1fr)!important;gap:8px!important;width:100%!important;max-width:none!important;margin:0!important;padding:12px 16px calc(12px + env(safe-area-inset-bottom,0px))!important;background:#fff!important;border:0!important;border-top:1px solid #a9a9a9!important;border-radius:0!important;box-shadow:0 -2px 10px rgba(0,0,0,.06)!important}
.product-sticky-form__card:has(>${TAG}.is-ready)>.product-sticky-form__variant{display:none!important}
.product-sticky-form__card:has(>${TAG}.is-ready)>${TAG}{display:flex;align-items:center;gap:12px;order:-1;min-width:0}
${TAG} .sbm-info{flex:1 1 auto;min-width:0;display:grid;gap:2px}
${TAG} .sbm-title{margin:0;font-size:15px;line-height:1.25;font-weight:700;color:#171717;letter-spacing:normal;text-transform:none;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:2;overflow:hidden;overflow-wrap:break-word}
${TAG} .sbm-meta{margin:0;font-size:14px;line-height:1.3;font-weight:400;color:#5f5f5f;letter-spacing:normal;text-transform:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${TAG} .sbm-meta.is-prompt{font-weight:700;color:#171717}
${TAG} .sbm-meta[hidden]{display:none!important}
${TAG} .sbm-cta{flex:0 0 auto;position:relative;display:inline-flex;align-items:center;justify-content:center;min-width:132px;min-height:48px;margin:0;padding:0 20px;border:0;border-radius:6px;background:#171717;color:#fff;opacity:1;font:inherit;font-size:16px;line-height:1.2;font-weight:500;letter-spacing:normal;text-transform:none;text-decoration:none;white-space:nowrap;box-shadow:none;cursor:pointer;-webkit-tap-highlight-color:transparent;touch-action:manipulation;transition:transform .12s ease,background-color .15s ease}
${TAG} .sbm-cta:active{transform:scale(.98)}
${TAG} .sbm-cta:focus-visible{outline:2px solid #171717;outline-offset:2px}
${TAG} .sbm-cta:disabled{background:#e8e8e8;color:#6b6b6b;cursor:not-allowed;transform:none}
${TAG} .sbm-cta.is-busy{color:transparent;pointer-events:none}
${TAG} .sbm-cta.is-busy::after{content:"";position:absolute;inset:0;margin:auto;width:18px;height:18px;box-sizing:border-box;border-radius:50%;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;animation:sbm-spin .7s linear infinite}
@media (prefers-reduced-motion:reduce){${TAG} .sbm-cta{transition:none}${TAG} .sbm-cta.is-busy::after{animation-duration:1.6s}}
}
@keyframes sbm-spin{to{transform:rotate(360deg)}}
`;

  const injectStyles = () => {
    if (document.getElementById('sticky-buy-mobile-css')) return;
    const style = document.createElement('style');
    style.id = 'sticky-buy-mobile-css';
    style.textContent = CSS;
    document.head.appendChild(style);
  };

  const upperFirst = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);
  const isRendered = (el) => !!el && el.getClientRects().length > 0;
  const isUsable = (button) =>
    !!button && button.isConnected && !button.disabled && button.getAttribute('aria-disabled') !== 'true';
  const isLoading = (button) =>
    !!button &&
    (button.getAttribute('aria-disabled') === 'true' ||
      button.getAttribute('aria-busy') === 'true' ||
      button.classList.contains('loading') ||
      button.classList.contains('is-loading'));

  const productCache = new WeakMap();
  const readProductJson = (script) => {
    if (!script) return null;
    if (productCache.has(script)) return productCache.get(script);
    let product = null;
    try {
      product = JSON.parse(script.textContent);
    } catch (error) {
      product = null;
    }
    productCache.set(script, product);
    return product;
  };

  // The header on mobile is sticky, so the picker is scrolled to just below whatever is
  // pinned to the top of the viewport instead of underneath it.
  const topInset = () => {
    let inset = 0;
    const x = Math.round(window.innerWidth / 2);
    [1, 12].forEach((y) => {
      (document.elementsFromPoint ? document.elementsFromPoint(x, y) : []).forEach((node) => {
        for (let el = node; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
          const position = getComputedStyle(el).position;
          if (position === 'fixed' || position === 'sticky') {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 1 && rect.bottom > inset && rect.bottom < window.innerHeight / 2) inset = rect.bottom;
            break;
          }
        }
      });
    });
    return inset;
  };

  class StickyBuyMobile extends HTMLElement {
    connectedCallback() {
      if (!this.built) this.build();

      this.host = this.closest('product-sticky-form');
      this.scope = this.closest('.shopify-section') || (this.host && this.host.parentElement) || document.body;

      this.onSchedule = this.onSchedule || (() => this.schedule());
      this.onChange = this.onChange || ((event) => {
        const target = event.target;
        if (target && target.classList && target.classList.contains('custom-option-buttons') && this.scope.contains(target)) {
          this.schedule();
        }
      });
      this.onSwitched = this.onSwitched || (() => {
        this.schedule();
        // add-to-cart-button.js clears the size 100ms after a colour switch.
        this.later(150);
        this.later(600);
      });
      this.onCartDone = this.onCartDone || (() => {
        this.clearBusy();
        this.schedule();
      });
      this.onPageShow = this.onPageShow || ((event) => {
        if (event.persisted) this.clearBusy();
        this.schedule();
      });

      document.addEventListener('change', this.onChange, true);
      document.addEventListener('product:switched', this.onSwitched);
      document.addEventListener('cart:updated', this.onCartDone);
      window.addEventListener('pageshow', this.onPageShow);
      if (MOBILE.addEventListener) MOBILE.addEventListener('change', this.onSchedule);
      else if (MOBILE.addListener) MOBILE.addListener(this.onSchedule);

      this.subscribeOnce();

      this.observer = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'disabled', 'aria-disabled', 'data-has-size-option']
      });

      this.schedule();
      this.later(250);
      this.later(1000);
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.onChange, true);
      document.removeEventListener('product:switched', this.onSwitched);
      document.removeEventListener('cart:updated', this.onCartDone);
      window.removeEventListener('pageshow', this.onPageShow);
      if (MOBILE.removeEventListener) MOBILE.removeEventListener('change', this.onSchedule);
      else if (MOBILE.removeListener) MOBILE.removeListener(this.onSchedule);
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.clearBusy();
      // The theme replaced the sticky form (colour switch, section re-render): put a fresh
      // bar into whatever card exists now.
      queueEnsure();
    }

    build() {
      this.built = true;
      this.className = 'sbm';

      const info = document.createElement('div');
      info.className = 'sbm-info';
      this.titleEl = document.createElement('p');
      this.titleEl.className = 'sbm-title';
      this.metaEl = document.createElement('p');
      this.metaEl.className = 'sbm-meta';
      this.metaEl.hidden = true;
      info.append(this.titleEl, this.metaEl);

      this.cta = document.createElement('button');
      this.cta.type = 'button';
      this.cta.className = 'sbm-cta';
      this.labelEl = document.createElement('span');
      this.labelEl.className = 'sbm-label';
      this.cta.appendChild(this.labelEl);
      this.cta.addEventListener('click', (event) => this.onClick(event));

      this.append(info, this.cta);
    }

    subscribeOnce() {
      if (this.subscribed) return;
      const pubsub = window.theme && window.theme.pubsub;
      const events = pubsub && pubsub.PUB_SUB_EVENTS;
      if (!pubsub || !events || typeof pubsub.subscribe !== 'function') return;
      this.subscribed = true;
      const guard = (fn) => () => {
        if (this.isConnected) fn();
      };
      if (events.variantChange) pubsub.subscribe(events.variantChange, guard(() => this.schedule()));
      if (events.cartUpdate) pubsub.subscribe(events.cartUpdate, guard(() => this.onCartDone()));
      if (events.cartError) pubsub.subscribe(events.cartError, guard(() => this.onCartDone()));
    }

    later(ms) {
      setTimeout(() => {
        if (this.isConnected) this.schedule();
      }, ms);
    }

    schedule() {
      if (this.frame || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.isConnected || !MOBILE.matches) return;
        try {
          this.render(this.readState());
          this.classList.add('is-ready');
        } catch (error) {
          // Hand the bar back to the theme rather than show something half-built.
          this.classList.remove('is-ready');
          if (!this.warned && window.console) console.warn('[sticky-buy-mobile]', error);
          this.warned = true;
        }
      });
    }

    readState() {
      const scope = this.scope;
      const host = this.host;
      const themeButton = host ? host.querySelector('.sticky_form__submit') : null;
      const main = scope.querySelector('.product-form__submit');
      const formId = (themeButton && themeButton.getAttribute('form')) || (host && host.getAttribute('form'));
      const form = formId ? document.getElementById(formId) : null;
      const idInput = form ? form.querySelector('input[name="id"]') : null;

      // Option name -> checked value (null when the option has radios but none is checked).
      const checked = {};
      const order = [];
      const counts = {};
      scope.querySelectorAll('input.custom-option-buttons[data-opt-name]').forEach((radio) => {
        const name = radio.getAttribute('data-opt-name');
        if (!(name in checked)) {
          checked[name] = null;
          order.push(name);
        }
        counts[name] = (counts[name] || 0) + 1;
        if (radio.checked) checked[name] = radio.value;
      });

      // Every Size/Width with a real choice has to be picked, same rule as add-to-cart-button.js.
      // A single-value option is always marked, so it never holds the bar back.
      const gateNames = GATE_OPTIONS.filter((name) => name in checked);
      const gated = gateNames.length > 0 && !!main && main.getAttribute('data-has-size-option') === 'true';
      const missing = gateNames.find((name) => checked[name] === null && counts[name] > 1) || null;
      const gateChosen = gateNames.every((name) => checked[name] !== null || counts[name] <= 1);
      const mode = gated ? (gateChosen ? 'ready' : 'select') : 'pass';

      const product = this.readProduct(host, scope);
      const variant = this.matchVariant(product, checked);

      const rawTitle =
        (product && product.title) ||
        (host && host.querySelector('.product-sticky-form__variant p') && host.querySelector('.product-sticky-form__variant p').textContent) ||
        '';
      const title = rawTitle.split(' - ')[0].trim();

      const themeLabel = themeButton ? themeButton.querySelector('[data-default-text]') : null;
      let addLabel = (themeLabel && themeLabel.getAttribute('data-default-text')) || TEXT.addToCart;
      if (themeButton && themeButton.hasAttribute('data-pre-order')) addLabel = TEXT.preOrder;
      const soldLabel = (themeLabel && themeLabel.getAttribute('data-sold-out')) || TEXT.soldOut;

      const state = { mode, scope, host, themeButton, main, idInput, checked, gateNames, missing, variant, title };

      if (mode === 'select') {
        const prompt = missing === 'Width' ? TEXT.selectWidth : TEXT.selectSize;
        return Object.assign(state, { meta: prompt, prompt: true, label: prompt, disabled: false });
      }

      const soldOut = !!variant && variant.available === false;
      const label = soldOut ? soldLabel : addLabel;

      if (mode === 'ready') {
        const size = checked.Size;
        const width = checked.Width ? upperFirst(checked.Width) : null;
        let meta = '';
        if (size && width) meta = `Size: ${size}, ${width}`;
        else if (size) meta = `Size: ${size}`;
        else if (width) meta = `Width: ${width}`;
        return Object.assign(state, { meta, prompt: false, label, disabled: soldOut });
      }

      // pass: no size gate on this template (FITFinder, accessories without a size option).
      let meta = '';
      if (!(themeButton && themeButton.getAttribute('data-has-size-option') === 'true')) {
        const values = order.map((name) => checked[name]).filter((value) => value !== null);
        const themeOptions = host ? host.querySelector('[data-sticky-product-options]') : null;
        meta = values.length ? values.join(' / ') : themeOptions ? themeOptions.textContent.trim() : '';
      }
      return Object.assign(state, { meta, prompt: false, label, disabled: soldOut });
    }

    readProduct(host, scope) {
      const scripts = Array.from(scope.querySelectorAll('script[type="application/json"][data-product-json]'));
      if (!scripts.length) return null;
      const idMatch = host && host.id ? host.id.match(/-(\d+)$/) : null;
      if (idMatch) {
        for (const script of scripts) {
          const product = readProductJson(script);
          if (product && String(product.id) === idMatch[1]) return product;
        }
      }
      return readProductJson(scripts[scripts.length - 1]);
    }

    matchVariant(product, checked) {
      if (!product || !Array.isArray(product.variants) || !Array.isArray(product.options)) return null;
      const names = product.options.map((option) => (option && typeof option === 'object' ? option.name : option));
      const wanted = [];
      for (const name of names) {
        if (!(name in checked)) {
          wanted.push(undefined); // no control on the page for this option
        } else if (checked[name] === null) {
          return null; // not chosen yet, so the variant is not known
        } else {
          wanted.push(String(checked[name]).trim());
        }
      }
      const matches = product.variants.filter((variant) =>
        wanted.every((value, index) => {
          if (value === undefined) return true;
          const own = Array.isArray(variant.options) ? variant.options[index] : variant[`option${index + 1}`];
          return own !== undefined && own !== null && String(own).trim() === value;
        })
      );
      return matches.length === 1 ? matches[0] : null;
    }

    render(state) {
      this.state = state;
      if (this.titleEl.textContent !== state.title) this.titleEl.textContent = state.title;

      const showMeta = !!state.meta;
      if (this.metaEl.hidden === showMeta) this.metaEl.hidden = !showMeta;
      if (showMeta && this.metaEl.textContent !== state.meta) this.metaEl.textContent = state.meta;
      if (this.metaEl.classList.contains('is-prompt') !== state.prompt) this.metaEl.classList.toggle('is-prompt', state.prompt);

      if (this.labelEl.textContent !== state.label) this.labelEl.textContent = state.label;
      if (this.cta.disabled !== state.disabled) this.cta.disabled = state.disabled;
      if (this.getAttribute('data-mode') !== state.mode) this.setAttribute('data-mode', state.mode);
    }

    onClick(event) {
      event.preventDefault();
      if (this.busy) return;

      let state;
      try {
        state = this.readState();
        this.render(state);
      } catch (error) {
        return;
      }

      if (state.mode === 'select') this.revealPicker(state);
      else if (state.mode === 'pass') this.forward(state);
      else if (!state.disabled) this.submitWhenSettled();
    }

    revealPicker(state) {
      const name = state.missing || ('Size' in state.checked ? 'Size' : state.gateNames[0]);
      const radio = state.scope.querySelector(`input.custom-option-buttons[data-opt-name="${name}"]`);
      const fieldset = radio ? radio.closest('fieldset') : null;
      const target = isRendered(fieldset) ? fieldset : state.scope.querySelector('product-info') || state.scope;

      const offset = Math.max(topInset(), 72) + 16;
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });

      if (target !== fieldset) return;
      const dropdown = fieldset.querySelector('size-dropdown');
      if (isRendered(dropdown) && typeof dropdown.open === 'function') {
        dropdown.open();
        return;
      }
      const swatch = fieldset.querySelector('label.label-swatch:not([aria-disabled="true"])');
      if (swatch) swatch.focus({ preventScroll: true });
    }

    // Templates without the size gate: press the theme button exactly as a tap on it would.
    forward(state) {
      const button = state.themeButton;
      if (!button) return;
      if (!button.disabled && !button.classList.contains('disabled-btn')) this.setBusy([button, state.main]);
      button.click();
    }

    // A size was just picked: the theme re-renders the form and swaps the hidden variant id
    // a moment later, so wait for that before pressing, or the previous size would be added.
    submitWhenSettled() {
      this.setBusy([]);
      const deadline = Date.now() + 2500;

      const tick = () => {
        this.pollTimer = 0;
        if (!this.isConnected) return this.clearBusy();

        let state;
        try {
          state = this.readState();
        } catch (error) {
          return this.clearBusy();
        }
        if (state.mode !== 'ready' || state.disabled) {
          this.clearBusy();
          return this.schedule();
        }

        const idKnown = !!state.variant && !!state.idInput;
        const idMatches = idKnown && String(state.idInput.value) === String(state.variant.id);
        const loading = isLoading(state.main) || isLoading(state.themeButton);
        const settled = idKnown ? idMatches && !loading : isUsable(state.main);

        if (!settled) {
          if (Date.now() < deadline) {
            this.pollTimer = setTimeout(tick, 100);
            return;
          }
          // Never add a size the shopper did not pick.
          return this.clearBusy();
        }

        this.press(state);
      };

      tick();
    }

    press(state) {
      const button = state.themeButton;
      if (button && button.isConnected && !button.disabled) {
        // add-to-cart-button.js leaves .disabled-btn on the sticky button after a size is
        // picked, which makes dynamic.js cancel the tap. Lift it for this one synchronous
        // click (the submit event fires inside click()), then put it back so desktop keeps
        // behaving as it does today.
        const lifted = button.classList.contains('disabled-btn');
        if (lifted) button.classList.remove('disabled-btn');
        try {
          button.click();
        } finally {
          if (lifted) button.classList.add('disabled-btn');
        }
        this.setBusy([button, state.main]);
        return;
      }
      if (isUsable(state.main)) {
        state.main.click();
        this.setBusy([state.main]);
        return;
      }
      this.clearBusy();
    }

    setBusy(watch) {
      this.clearTimers();
      this.busy = true;
      this.cta.classList.add('is-busy');
      this.cta.setAttribute('aria-busy', 'true');

      const buttons = watch.filter(Boolean);
      if (!buttons.length) return; // still waiting on the variant; submitWhenSettled owns it

      const started = Date.now();
      let seen = false;
      const check = () => {
        const busyNow = buttons.some((button) => button.isConnected && isLoading(button));
        if (busyNow) seen = true;
        const elapsed = Date.now() - started;
        if ((seen && !busyNow) || (!seen && elapsed > 1500) || elapsed > 8000 || !this.isConnected) {
          this.clearBusy();
          return;
        }
        this.watchTimer = setTimeout(check, 150);
      };
      this.watchTimer = setTimeout(check, 150);
    }

    clearTimers() {
      if (this.watchTimer) clearTimeout(this.watchTimer);
      if (this.pollTimer) clearTimeout(this.pollTimer);
      this.watchTimer = 0;
      this.pollTimer = 0;
    }

    clearBusy() {
      this.clearTimers();
      if (!this.busy) return;
      this.busy = false;
      if (this.cta) {
        this.cta.classList.remove('is-busy');
        this.cta.removeAttribute('aria-busy');
      }
    }
  }

  const ensureAll = () => {
    document.querySelectorAll('product-sticky-form .product-sticky-form__card').forEach((card) => {
      if (!card.querySelector('.sticky_form__submit')) return;
      if (Array.from(card.children).some((child) => child.localName === TAG)) return;
      card.appendChild(document.createElement(TAG));
    });
  };

  let ensureTimers = [];
  const queueEnsure = () => {
    ensureTimers.forEach(clearTimeout);
    ensureTimers = [0, 300, 1200].map((delay) => setTimeout(ensureAll, delay));
  };

  injectStyles();
  if (!customElements.get(TAG)) customElements.define(TAG, StickyBuyMobile);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueEnsure);
  else queueEnsure();
  document.addEventListener('product:switched', queueEnsure);
  document.addEventListener('shopify:section:load', queueEnsure);
  window.addEventListener('pageshow', queueEnsure);
})();